/**
 * เอกสารบริษัท (Company Document Vault) — หน้า owner/hr.
 * แท็บ "เอกสาร": ทะเบียนเอกสารบริษัท + ตราประทับ/ลายเซ็น
 * แท็บ "คำขอ": รายการคำขอสำเนาของพนักงาน (คลิกแถว → หน้ารายละเอียดเต็มหน้า)
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { HubHeader, PillTabs } from '../../components/layout/HubHeader';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { uploadFile } from '../../lib/api-client';
import {
  useCompanyDocuments,
  useCreateCompanyDocument,
  useUpdateCompanyDocument,
  useDeleteCompanyDocument,
  useDownloadCompanyDocument,
  useCompanyDocRequests,
  useCompanyProfile,
  useUpdateCompanyProfile,
  type CompanyDocument,
} from '../../hooks';
import { FileText, Plus, Download, Pencil, Trash2, Stamp, PenLine, AlertTriangle } from 'lucide-react';
import { openWhenReady } from '../../lib/open-when-ready';

type Tab = 'documents' | 'requests';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'documents', label: 'เอกสาร' },
  { key: 'requests', label: 'คำขอ' },
];

function formatBytes(n: number | null): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function expiryBadge(expiresAt: string | null) {
  if (!expiresAt) return null;
  const days = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
  if (days < 0) {
    return <span className="whitespace-nowrap text-xs font-medium text-red-700 bg-red-50 rounded-full px-2.5 py-1">หมดอายุ</span>;
  }
  if (days <= 30) {
    return <span className="whitespace-nowrap text-xs font-medium text-amber-700 bg-amber-50 rounded-full px-2.5 py-1">ใกล้หมดอายุ</span>;
  }
  return null;
}

export function OwnerCompanyDocs() {
  const [tab, setTab] = useState<Tab>(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '') as Tab;
    return h === 'requests' ? 'requests' : 'documents';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${tab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  }, [tab]);
  const loc = useLocation();
  useEffect(() => {
    const h = loc.hash.replace('#', '') as Tab;
    if (h === 'documents' || h === 'requests') setTab(h);
  }, [loc.hash]);

  return (
    <AdminLayout title="เอกสารบริษัท">
      <HubHeader section="หนังสือรับรอง" title="เอกสารบริษัท" subtitle="เก็บเอกสารสำคัญบริษัท + จัดการคำขอสำเนาจากพนักงาน" />
      <PillTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'documents' && <DocumentsTab />}
      {tab === 'requests' && <RequestsTab />}
    </AdminLayout>
  );
}

function DocumentsTab() {
  const { data, isLoading } = useCompanyDocuments();
  const createM = useCreateCompanyDocument();
  const updateM = useUpdateCompanyDocument();
  const deleteM = useDeleteCompanyDocument();
  const downloadM = useDownloadCompanyDocument();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CompanyDocument | null>(null);

  const documents = [...(data?.documents ?? [])].sort((a, b) => a.sort - b.sort);

  async function handleDownload(id: string) {
    try {
      await openWhenReady(async () => (await downloadM.mutateAsync(id)).url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดไม่สำเร็จ');
    }
  }

  async function handleDelete(doc: CompanyDocument) {
    if (!window.confirm(`ลบ "${doc.label}" ใช่ไหม?`)) return;
    try {
      await deleteM.mutateAsync(doc.id);
      toast.success('ลบเอกสารแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ — อาจมีคำขอเอกสารนี้อยู่ ลองพักไว้แทน (แก้เอกสาร → ปิดใช้งาน)');
    }
  }

  return (
    <div className="space-y-4">
      <StampSection />

      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="btn-cta">
          <Plus className="w-4 h-4" /> เอกสาร
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : documents.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ยังไม่มีเอกสารบริษัท</p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500 text-xs">
                <th className="text-left py-2 px-3">เอกสาร</th>
                <th className="text-left py-2 px-2">วันที่บนเอกสาร</th>
                <th className="text-left py-2 px-2">วันหมดอายุ</th>
                <th className="text-left py-2 px-2">ขนาด</th>
                <th className="text-left py-2 px-2">สถานะ</th>
                <th className="text-right py-2 px-3">จัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {documents.map((d) => (
                <tr key={d.id}>
                  <td className="py-2 px-3">
                    <div className="font-medium flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      {d.label}
                    </div>
                    {(d.note || d.filename) && (
                      <div className="text-xs text-gray-400 truncate max-w-[280px]">
                        {[d.note, d.filename].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    {d.doc_date ? new Date(d.doc_date).toLocaleDateString('th-TH') : '—'}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {d.expires_at ? new Date(d.expires_at).toLocaleDateString('th-TH') : 'ไม่หมดอายุ'}
                      {expiryBadge(d.expires_at)}
                    </div>
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap text-gray-500">{formatBytes(d.size_bytes)}</td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    {d.active
                      ? <span className="text-xs font-medium text-green-700 bg-green-50 rounded-full px-2.5 py-1 whitespace-nowrap">ใช้อยู่</span>
                      : <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-1 whitespace-nowrap">พักไว้</span>}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleDownload(d.id)} title="ดาวน์โหลดต้นฉบับ" className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditing(d)} title="แก้ไข" className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(d)} title="ลบ" className="p-1.5 rounded hover:bg-gray-100 text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showForm || editing) && (
        <DocumentFormModal
          doc={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSubmit={async (body) => {
            try {
              if (editing) {
                await updateM.mutateAsync({ id: editing.id, ...body });
                toast.success('บันทึกการแก้ไขแล้ว');
              } else {
                if (!body.r2_path) { toast.error('ไม่พบไฟล์ที่อัปโหลด'); return; }
                await createM.mutateAsync({ ...body, r2_path: body.r2_path });
                toast.success('เพิ่มเอกสารแล้ว');
              }
              setShowForm(false);
              setEditing(null);
            } catch (e) {
              toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
            }
          }}
        />
      )}
    </div>
  );
}

function DocumentFormModal({
  doc,
  onClose,
  onSubmit,
}: {
  doc: CompanyDocument | null;
  onClose: () => void;
  onSubmit: (body: {
    label: string; note?: string; r2_path?: string; filename?: string; mime?: string;
    size_bytes?: number; doc_date?: string; expires_at?: string; sort?: number; active?: boolean;
  }) => Promise<void>;
}) {
  const [label, setLabel] = useState(doc?.label ?? '');
  const [note, setNote] = useState(doc?.note ?? '');
  const [docDate, setDocDate] = useState(doc?.doc_date ?? '');
  const [expiresAt, setExpiresAt] = useState(doc?.expires_at ?? '');
  const [sort, setSort] = useState(doc?.sort ?? 0);
  const [active, setActive] = useState(doc?.active ?? true);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function handleSave() {
    if (!label.trim()) { toast.error('กรุณาระบุชื่อเอกสาร'); return; }
    if (!doc && !file) { toast.error('กรุณาเลือกไฟล์เอกสาร'); return; }
    setSaving(true);
    try {
      let filePayload: { r2_path?: string; filename?: string; mime?: string; size_bytes?: number } = {};
      if (file) {
        setUploading(true);
        const path = await uploadFile(file, 'company', file.name);
        setUploading(false);
        filePayload = { r2_path: path, filename: file.name, mime: file.type || undefined, size_bytes: file.size };
      }
      await onSubmit({
        label: label.trim(),
        note: note.trim() || undefined,
        doc_date: docDate || undefined,
        expires_at: expiresAt || undefined,
        sort,
        active: doc ? active : undefined,
        ...filePayload,
      });
    } finally {
      setSaving(false);
      setUploading(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={doc ? 'แก้ไขเอกสาร' : 'เพิ่มเอกสารบริษัท'}>
      <div className="space-y-3">
        <div>
          <label className="label">ชื่อเอกสาร *</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="เช่น หนังสือรับรองบริษัท" className="input w-full" />
        </div>
        <div>
          <label className="label">ไฟล์เอกสาร {doc ? '(ไม่เลือก = ใช้ไฟล์เดิม)' : '*'}</label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={uploading}
            className="text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">วันที่บนเอกสาร</label>
            <input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">วันหมดอายุ</label>
            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="input w-full" />
          </div>
        </div>
        <div>
          <label className="label">หมายเหตุ</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className="input w-full" />
        </div>
        <div>
          <label className="label">ลำดับการแสดง</label>
          <input type="number" value={sort} onChange={(e) => setSort(Number(e.target.value) || 0)} className="input w-full" />
        </div>
        {doc && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            ใช้งานอยู่ (ยกเลิกติ๊ก = พักไว้)
          </label>
        )}
        <button onClick={handleSave} disabled={saving} className="btn-cta w-full">
          {saving ? (uploading ? 'กำลังอัปโหลด…' : 'กำลังบันทึก…') : 'บันทึก'}
        </button>
      </div>
    </Modal>
  );
}

function StampSection() {
  const { data: profile } = useCompanyProfile();
  const updateM = useUpdateCompanyProfile();
  const toast = useToast();
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);

  const hasStamp = !!profile?.company_stamp_path;
  const hasSignature = !!profile?.signature_path;

  async function handleUpload(kind: 'seal' | 'signature', file: File | null) {
    if (!file) return;
    const setBusy = kind === 'seal' ? setUploadingStamp : setUploadingSig;
    setBusy(true);
    try {
      const path = await uploadFile(file, kind, file.name);
      await updateM.mutateAsync(kind === 'seal' ? { company_stamp_path: path } : { signature_path: path });
      toast.success(kind === 'seal' ? 'อัปโหลดตราประทับแล้ว' : 'อัปโหลดลายเซ็นแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'อัปโหลดไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4 space-y-3">
      <div className="font-semibold flex items-center gap-2">
        <Stamp className="w-4 h-4 text-gray-400" /> ตราประทับ / ลายเซ็น
      </div>
      {(!hasStamp || !hasSignature) && (
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>ยังไม่มี{!hasStamp && !hasSignature ? 'ตราประทับและลายเซ็น' : !hasStamp ? 'ตราประทับ' : 'ลายเซ็น'} — ประทับตราตอนอนุมัติคำขอจะยังใช้ไม่ได้เต็มรูปแบบ</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label flex items-center gap-1"><Stamp className="w-3.5 h-3.5" /> ตราประทับ (PNG โปร่งใส)</label>
          {profile?.company_stamp_url && (
            <img src={profile.company_stamp_url} alt="ตราประทับ" className="h-16 object-contain mb-1 bg-gray-50 rounded border border-gray-100 p-1" />
          )}
          <input
            type="file"
            accept="image/png"
            onChange={(e) => handleUpload('seal', e.target.files?.[0] ?? null)}
            disabled={uploadingStamp}
            className="text-sm"
          />
        </div>
        <div>
          <label className="label flex items-center gap-1"><PenLine className="w-3.5 h-3.5" /> ลายเซ็นผู้มีอำนาจ (PNG โปร่งใส)</label>
          {profile?.signature_url && (
            <img src={profile.signature_url} alt="ลายเซ็น" className="h-16 object-contain mb-1 bg-gray-50 rounded border border-gray-100 p-1" />
          )}
          <input
            type="file"
            accept="image/png"
            onChange={(e) => handleUpload('signature', e.target.files?.[0] ?? null)}
            disabled={uploadingSig}
            className="text-sm"
          />
        </div>
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'รออนุมัติ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ปฏิเสธ',
  canceled: 'ยกเลิก',
};
const STATUS_CLASS: Record<string, string> = {
  pending: 'text-amber-700 bg-amber-50',
  approved: 'text-green-700 bg-green-50',
  rejected: 'text-red-700 bg-red-50',
  canceled: 'text-gray-500 bg-gray-100',
};

function RequestsTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const { data, isLoading } = useCompanyDocRequests(statusFilter || undefined);
  const navigate = useNavigate();
  const requests = data?.requests ?? [];

  return (
    <div className="space-y-3">
      <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input">
        <option value="">— ทุกสถานะ —</option>
        <option value="pending">รออนุมัติ</option>
        <option value="approved">อนุมัติแล้ว</option>
        <option value="rejected">ปฏิเสธ</option>
        <option value="canceled">ยกเลิก</option>
      </select>

      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : requests.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ไม่มีคำขอ</p>
      ) : (
        <div className="card divide-y divide-gray-100">
          {requests.map((r) => (
            <button
              key={r.id}
              onClick={() => navigate(`/owner/company-doc-requests/${r.id}`)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-400">{r.req_no}</span>
                  {r.emp_name ?? r.emp_code}
                </div>
                <div className="text-xs text-gray-500 truncate max-w-[420px]">{r.purpose}</div>
              </div>
              <span className={`whitespace-nowrap text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_CLASS[r.status] ?? ''}`}>
                {STATUS_LABEL[r.status] ?? r.status}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
